import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";

/**
 * Listens for `?ref=CODE` on every visit, stamps it into localStorage so a
 * later registration/checkout can credit the referrer. If the visitor is
 * already signed in we capture it server-side immediately. Returns null —
 * it only runs side effects.
 */
export default function ReferralCapture() {
  const { user } = useAuth();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    try {
      const url = new URL(window.location.href);
      const incoming = (url.searchParams.get("ref") || "").trim();
      // Brand attribution — when ref looks like "brand_{slug}" (set by partner
      // store footers and product links) we stash the slug separately so the
      // checkout flow can credit the brand. The classic referral code path
      // continues unaffected for everything else.
      const brandMatch = incoming.match(/^brand_([a-z0-9-]{3,40})$/i);
      if (brandMatch) {
        const slug = brandMatch[1].toLowerCase();
        try {
          localStorage.setItem("motowear_brand_slug", slug);
          // Expire after 30 days
          localStorage.setItem("motowear_brand_slug_until", String(Date.now() + 30 * 86400 * 1000));
        } catch (_e) { /* ignore */ }
        url.searchParams.delete("ref");
        window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);
        return;
      }
      const incomingUpper = incoming.toUpperCase();
      const stored = (localStorage.getItem("motowear_ref") || "").trim().toUpperCase();
      const ref = incomingUpper || stored;
      if (incomingUpper) {
        localStorage.setItem("motowear_ref", incomingUpper);
        url.searchParams.delete("ref");
        window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);
      }
      if (ref && user?.id) {
        client.post("/referral/capture", { ref_code: ref })
          .then((r) => {
            if (!r.data?.ok) {
              localStorage.removeItem("motowear_ref");
            }
          })
          .catch(() => {});
      }
    } catch (e) {
      // Non-fatal — referral capture is best-effort.
    }
  }, [user?.id]);

  return null;
}
